<script setup lang="ts">
import { onMounted, ref } from 'vue';
import DialogTemplate from '@/core/components/common/DialogTemplate.vue';
import Skeleton from 'primevue/skeleton';
import type { GStats } from '@/modules/recruitment/models';
import { isAxiosError } from 'axios';
import { useToast } from 'primevue/usetoast';
import { http } from '@/plugins/axios';

const toast = useToast();
const loading = ref(true);
const gStats = ref<GStats|null>(null);

const fetchGlobalStats = async () => {
  try {
    gStats.value = null;
    loading.value = true;
    const resp = await http.get('/stats/global');
    if (isAxiosError(resp)) {
      throw resp;
    }
    if (resp.data && resp.data.content) {
      gStats.value = resp.data.content;
    }
  } catch (error) {
    if (isAxiosError(error) && error.status === 404) {
      toast.add({
        severity: 'error',
        summary: 'Stats not found',
        detail: 'Please report this issue to your local dev team',
        life: 5000,
      });
      return;
    }
    toast.add({
      severity: 'error',
      summary: 'Fetching global stats failed',
      detail: error,
      life: 5000,
    });
    return;
  } finally {
    loading.value = false;
  }
};

const modalStyles = ref({
  height: '100%',
  width: '100%',
});

onMounted(() => { fetchGlobalStats(); });

const numFmt = new Intl.NumberFormat('de-DE', {
  useGrouping: true,
  maximumFractionDigits: 0, // ignore decimals
});

const fmtNum = (num: number|null|undefined) => {
  return num == null ? '0' : numFmt.format(num);
};
</script>

<template>
  <DialogTemplate title="Global recruitment stats" :footer="false">
    <div class="flex flex-col overflow-hidden h-full" :style="modalStyles">
      <div v-if="loading" class="flex flex-col gap-1">
        <Skeleton height="1rem" />
        <Skeleton height="3rem" />
        <Skeleton height="1rem" />
        <Skeleton height="12rem" />
      </div>
      <div v-else class="flex flex-col gap-1 mb-4">
        <!-- TODO: Maybe use a DataTable instead? -->
        <div class="text-lg font-semibold mt-4 p-1">Segments</div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800 border-t-1">
          <span>Closed year-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.segmentsClosedYTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1">
          <span>Closed month-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.segmentsClosedMTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800">
          <span>Closed week-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.segmentsClosedWTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 border-b-1">
          <span>Closed today</span>
          <span class="monospace">
            {{ fmtNum(gStats?.segmentsClosedToday) }}
          </span>
        </div>

        <div class="text-lg font-semibold mt-4 p-1">Interviews</div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800 border-t-1">
          <span>Accepted year-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsAcceptedYTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1">
          <span>Accepted month-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsAcceptedMTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800">
          <span>Accepted week-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsAcceptedWTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 border-b-1">
          <span>Accepted today</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsAcceptedToday) }}
          </span>
        </div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800">
          <span>Rejected year-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsRejectedYTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1">
          <span>Rejected month-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsRejectedMTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800">
          <span>Rejected week-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsRejectedWTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 border-b-1">
          <span>Rejected today</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsRejectedToday) }}
          </span>
        </div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800">
          <span>Marked Maybe year-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsMaybeYTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1">
          <span>Marked Maybe month-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsMaybeMTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 bg-gray-100 dark:bg-surface-800">
          <span>Marked Maybe week-to-date</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsMaybeWTD) }}
          </span>
        </div>
        <div class="flex justify-between p-1 border-b-1">
          <span>Marked Maybe today</span>
          <span class="monospace">
            {{ fmtNum(gStats?.interviewsMaybeToday) }}
          </span>
        </div>
      </div>
    </div>
  </DialogTemplate>
</template>

<style>
.monospace {
  font-family: monospace;
  font-size: 1rem;
}
</style>
